<div class="p-6 max-w-5xl mx-auto">
  <!-- Page header -->
  <div class="flex items-center justify-between mb-6">
    <div class="flex items-center gap-3">
      <div
        class="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center shadow-sm"
      >
        <lucide-icon [img]="Megaphone" class="w-5 h-5 text-white"></lucide-icon>
      </div>
      <div>
        <h1 class="text-xl font-bold text-gray-900 dark:text-white">Xabarnomalar</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400">{{ totalCount() }} ta xabar</p>
      </div>
    </div>
    <button
      (click)="openCreate()"
      class="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-all"
    >
      <lucide-icon [img]="Plus" class="w-4 h-4"></lucide-icon>
      Yangi xabar
    </button>
  </div>

  <!-- Table -->
  <div
    class="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden"
  >
    @if (loading()) {
      <div class="flex items-center justify-center py-16">
        <div
          class="w-7 h-7 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"
        ></div>
      </div>
    } @else if (items().length === 0) {
      <div class="flex flex-col items-center justify-center py-16 text-center px-6">
        <div
          class="w-16 h-16 rounded-2xl bg-gray-50 dark:bg-gray-800 flex items-center justify-center mb-4"
        >
          <lucide-icon [img]="Bell" class="w-8 h-8 text-gray-300 dark:text-gray-600"></lucide-icon>
        </div>
        <p class="text-gray-500 dark:text-gray-400 font-medium">Hali xabar yo'q</p>
        <p class="text-sm text-gray-400 dark:text-gray-600 mt-1">Yangi xabar yarating</p>
      </div>
    } @else {
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <th
              class="text-left px-5 py-3 font-semibold text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide"
            >
              Sarlavha
            </th>
            <th
              class="text-left px-4 py-3 font-semibold text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide w-28"
            >
              Tur
            </th>
            <th
              class="text-left px-4 py-3 font-semibold text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide w-24"
            >
              Holat
            </th>
            <th
              class="text-left px-4 py-3 font-semibold text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide w-28"
            >
              Yaratildi
            </th>
            <th
              class="text-left px-4 py-3 font-semibold text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide w-28"
            >
              Tugaydi
            </th>
            <th class="w-24 px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          @for (ann of items(); track ann.id) {
            <tr
              class="border-b border-gray-50 dark:border-gray-800/60 last:border-0 hover:bg-gray-50/70 dark:hover:bg-gray-800/30 transition-colors"
            >
              <td class="px-5 py-3.5">
                <div class="flex items-center gap-2.5">
                  @if (ann.isPinned) {
                    <lucide-icon
                      [img]="Bookmark"
                      class="w-3.5 h-3.5 text-emerald-500 shrink-0"
                    ></lucide-icon>
                  }
                  <div>
                    <p class="font-semibold text-gray-900 dark:text-white text-sm">
                      {{ ann.title }}
                    </p>
                    <p
                      class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 line-clamp-1 max-w-xs"
                    >
                      {{ ann.body }}
                    </p>
                  </div>
                </div>
              </td>
              <td class="px-4 py-3.5">
                <span
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold"
                  [class]="typeColor(ann.type)"
                >
                  <span>{{ typeMeta(ann.type).icon }}</span>
                  {{ typeMeta(ann.type).label }}
                </span>
              </td>
              <td class="px-4 py-3.5">
                <button
                  (click)="toggle(ann)"
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors"
                  [class]="
                    ann.isActive
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 hover:bg-emerald-200'
                      : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200'
                  "
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full"
                    [class]="ann.isActive ? 'bg-emerald-500' : 'bg-gray-400'"
                  ></span>
                  {{ ann.isActive ? 'Faol' : 'Nofaol' }}
                </button>
              </td>
              <td class="px-4 py-3.5 text-xs text-gray-500 dark:text-gray-400">
                {{ formatDate(ann.createdAt) }}
              </td>
              <td class="px-4 py-3.5 text-xs text-gray-500 dark:text-gray-400">
                {{ formatDate(ann.expiresAt) }}
              </td>
              <td class="px-4 py-3.5">
                <div class="flex items-center gap-1 justify-end">
                  <button
                    (click)="openEdit(ann)"
                    class="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  >
                    <lucide-icon [img]="Edit" class="w-4 h-4"></lucide-icon>
                  </button>
                  <button
                    (click)="delete(ann)"
                    class="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <lucide-icon [img]="Trash2" class="w-4 h-4"></lucide-icon>
                  </button>
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>

      <!-- Pagination -->
      @if (totalPages > 1) {
        <div
          class="flex items-center justify-between px-5 py-3 border-t border-gray-100 dark:border-gray-800"
        >
          <p class="text-xs text-gray-400">{{ items().length }} / {{ totalCount() }} ta</p>
          <div class="flex gap-1">
            <button
              (click)="goPage(page() - 1)"
              [disabled]="page() === 1"
              class="px-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              ←
            </button>
            @for (p of [].constructor(totalPages); track $index) {
              <button
                (click)="goPage($index + 1)"
                class="px-3 py-1.5 text-xs rounded-lg transition-colors"
                [class]="
                  page() === $index + 1
                    ? 'bg-emerald-500 text-white font-semibold'
                    : 'border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                "
              >
                {{ $index + 1 }}
              </button>
            }
            <button
              (click)="goPage(page() + 1)"
              [disabled]="page() === totalPages"
              class="px-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              →
            </button>
          </div>
        </div>
      }
    }
  </div>
</div>

<!-- ── Create / Edit Dialog ── -->
@if (dialogVisible) {
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4">
    <!-- Backdrop -->
    <div
      class="absolute inset-0 bg-black/40 backdrop-blur-sm"
      (click)="closeDialog()"
      (keydown.escape)="closeDialog()"
      role="button"
      tabindex="0"
      aria-label="Yopish"
    ></div>

    <!-- Dialog -->
    <div
      class="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 flex flex-col max-h-[90vh]"
      style="animation: dlgIn 220ms cubic-bezier(0.2, 0.8, 0.3, 1) both"
    >
      <!-- Dialog Header -->
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center"
          >
            <lucide-icon [img]="Megaphone" class="w-4 h-4 text-white"></lucide-icon>
          </div>
          <h2 class="text-base font-bold text-gray-900 dark:text-white">
            {{ isEdit ? 'Xabarni tahrirlash' : 'Yangi xabar' }}
          </h2>
        </div>
        <button
          (click)="closeDialog()"
          class="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition-colors"
        >
          <lucide-icon [img]="X" class="w-4 h-4"></lucide-icon>
        </button>
      </div>

      <!-- Dialog Body -->
      <div class="overflow-y-auto flex-1 px-6 py-5 space-y-4">
        <!-- Title -->
        <div>
          <label for="" class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5"
            >Sarlavha *</label
          >
          <input
            [(ngModel)]="form.title"
            type="text"
            placeholder="Xabar sarlavhasi..."
            class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
          />
        </div>

        <!-- Body -->
        <div>
          <label for="" class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5"
            >Matn *</label
          >
          <textarea
            [(ngModel)]="form.body"
            rows="4"
            placeholder="Xabar matni..."
            class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition resize-none"
          ></textarea>
        </div>

        <!-- Type -->
        <div>
          <label for="" class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5"
            >Tur</label
          >
          <div class="grid grid-cols-4 gap-2">
            @for (opt of typeOptions; track opt.value) {
              <button
                type="button"
                (click)="form.type = opt.value"
                class="flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl border-2 text-xs font-semibold transition-all"
                [class]="
                  form.type === opt.value
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                    : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'
                "
              >
                <span class="text-lg">{{ opt.icon }}</span>
                {{ opt.label }}
              </button>
            }
          </div>
        </div>

        <!-- Expires At -->
        <div>
          <label for="" class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5"
            >Tugash sanasi (ixtiyoriy)</label
          >
          <p-datepicker
            [(ngModel)]="form.expiresAt"
            dateFormat="dd.mm.yy"
            [showClear]="true"
            appendTo="body"
            placeholder="Tanlang..."
            class="w-full"
          />
        </div>

        <!-- Toggles -->
        <div class="flex items-center gap-4">
          <!-- Pinned -->
          <button
            type="button"
            (click)="form.isPinned = !form.isPinned"
            class="flex items-center gap-2 px-3.5 py-2 rounded-xl border-2 text-xs font-semibold transition-all"
            [class]="
              form.isPinned
                ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'
            "
          >
            <lucide-icon [img]="Bookmark" class="w-3.5 h-3.5"></lucide-icon>
            Muhim
          </button>

          <!-- Active (edit only) -->
          @if (isEdit) {
            <button
              type="button"
              (click)="form.isActive = !form.isActive"
              class="flex items-center gap-2 px-3.5 py-2 rounded-xl border-2 text-xs font-semibold transition-all"
              [class]="
                form.isActive
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                  : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'
              "
            >
              <span
                class="w-2 h-2 rounded-full"
                [class]="form.isActive ? 'bg-emerald-500' : 'bg-gray-400'"
              ></span>
              {{ form.isActive ? 'Faol' : 'Nofaol' }}
            </button>
          }
        </div>
      </div>

      <!-- Dialog Footer -->
      <div
        class="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-800"
      >
        <button
          (click)="closeDialog()"
          class="px-4 py-2 text-sm font-medium border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 transition-colors"
        >
          Bekor
        </button>
        <button
          (click)="save()"
          [disabled]="saving || !form.title.trim() || !form.body.trim()"
          class="px-5 py-2 text-sm font-semibold bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl shadow-sm disabled:opacity-50 transition-all"
        >
          {{ saving ? 'Saqlanmoqda...' : isEdit ? 'Saqlash' : 'Yuborish' }}
        </button>
      </div>
    </div>
  </div>
}

<style>
  @keyframes dlgIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(8px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
</style>
